import { QueryResponse } from "~/modules/filters/types";
import { AggregateItem } from "~/modules/token_timeseries/types";
import { Asset } from "~/modules/assets/types";
import { VisualizationCard } from "~/modules/visualization/components/VisualizationCard";
import { AssetNetFlowsChart } from "~/modules/visualization/components/AssetNetFlowsChart";
import { useState } from "react";
import { useUser } from "~/modules/user/hooks/useUser";
import { NetFlowsChartItem } from "~/modules/visualization/components/AssetNetFlowsChart";
import { RWA_COIN_ICON } from "~/consts";
import { formatDate } from "~/utils/functions/formatDate";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import { TimeframeSelect } from "~/components/TimeframeSelect";
import * as Analytics from "../../analytics";

// // // //

export function NetFlowsCard(props: {
    listQueryResponse: QueryResponse<Asset>;
    title?: string;
    netFlows: {
        seven_days: Array<AggregateItem>;
        thirty_days: Array<AggregateItem>;
        year_to_date: Array<AggregateItem>;
        ninety_days: Array<AggregateItem>;
        six_months: Array<AggregateItem>;
        one_year: Array<AggregateItem>;
    };
}) {
    const { listQueryResponse, netFlows, title = "Top Net Flows" } = props;
    const { isLoggedIn } = useUser();

    const [timeframe, setTimeframe] = useState<string>("30D");

    const customControls = (
        <div className="w-full flex items-start justify-end">
            <TimeframeSelect
                value={timeframe}
                disabled={!isLoggedIn}
                isLoggedIn={isLoggedIn}
                onChange={(updatedValue) => {
                    Analytics.trackChangeNetFlowsTimeframe({
                        prevValue: timeframe,
                        nextValue: updatedValue,
                    });

                    setTimeframe(updatedValue);
                }}
            />
        </div>
    );

    const mappedNetFlows: Record<string, AggregateItem[]> = {
        "7D": netFlows.seven_days,
        "30D": netFlows.thirty_days,
        YTD: netFlows.year_to_date,
        "90D": netFlows.ninety_days,
        "6M": netFlows.six_months,
        "1Y": netFlows.one_year,
    };

    const selectedNetFlows: NetFlowsChartItem[] = mappedNetFlows[timeframe].map(
        (item) => {
            const asset = listQueryResponse.results.find(
                (asset) => asset.id === item.group.id,
            ) as Asset;

            const protocol = asset?.tokens[0]?.protocol;

            return {
                ...item,
                content: {
                    name: asset?.name || item.group.name,
                    ticker: asset?.ticker || asset?.name || item.group.name,
                    icon_url: asset?.icon_url || RWA_COIN_ICON,
                    issuer_name: asset?.issuer_name || item.group.name,
                    issuer_icon_url: asset?.issuer?.icon_url || RWA_COIN_ICON,
                    protocol_name: protocol?.name || item.group.name,
                    protocol_icon_url: protocol?.icon_url || RWA_COIN_ICON,
                },
            };
        },
    );

    return (
        <VisualizationCard
            title={title}
            infoTooltip={
                <>
                    Net flows are calculated as the difference between tokens
                    minted and tokens burned.
                </>
            }
            subtitle={`As of ${formatDate(new Date())}`}
            cardClassName="lg:order-0 2xl:order-last"
            colSpans={{
                sm: 12,
                md: 12,
                lg: 12,
                xl: 6,
                xxl: 6,
            }}
            watermark="center-40pct"
            wrapperClassNameOverride=""
            showTimestamp={false}
            customControls={customControls}
        >
            <WindowResizeObserver defaultPlaceholderHeight={390} delay={1200}>
                <AssetNetFlowsChart items={selectedNetFlows} />
            </WindowResizeObserver>
        </VisualizationCard>
    );
}
